/* eslint-disable angular/window-service,angular/document-service */
import './material.min';
import './index.scss';
import { urlParam, renderCaptcha, validateCaptcha, isCaptchaActivated } from './utils.login';
import { Telemetry } from '../Telemetry/Telemetry';
import { applyCssVariables } from './themes.login';
import { redirectUriStorageItemKey, tokenStorageItemKey } from '../Auth/auth.consts';

window.mxLogin = new MxLogin();

// IE Polyfills
if (!Element.prototype.matches) {
	Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
}

if (!Element.prototype.closest) {
	Element.prototype.closest = function (s) {
		let el = this;

		do {
			if (Element.prototype.matches.call(el, s)) {
				return el;
			}
			el = el.parentElement || el.parentNode;
		} while (el !== null && el.nodeType === 1);
		return null;
	};
}

// IE bug fix.
if (!location.origin) {
	location.origin = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
}

const redirectedFromAccountsCookieName = '_REDIRECTED_FROM_M42ACCOUNTS';
const localStorageSignoutKey = 'mx.login.signout';

function MxLogin() {
	const mxLogin = this;
	let aadPanel;
	let telemetry;

	mxLogin.config = {}; /// get
	mxLogin.m42Settings = {};
	mxLogin.localStrings = {};
	mxLogin.isMainForm = true;
	const isWindows = navigator.appVersion.indexOf('Win') >= 0;

	const forceLoginPage = urlParam('ForceLoginPage') === 'true';
	let redirectUrl = urlParam('redirect_uri') || null;
	window.localStorage.removeItem(localStorageSignoutKey);
	const isSignOut = urlParam('redirect_to_signout') === 'true';
	const errorFromParams = urlParam('error') || null;

	const language = cookieValue('explicitLanguage') || navigator.language || navigator.userLanguage;
	const isLangDE = (language || '').toLowerCase().indexOf('de') !== -1;

	const defaultErrorText =
		window.localStorage.getItem('mx.login.defaultErrorText') ||
		(isLangDE ? 'Dienst ist nicht verfügbar. Bitte wenden Sie sich an den Systemadministrator.' : 'Service unavailable. Please contact system administrator.');

	const document = window.document;
	const get = document.getElementById.bind(document);

	const orgUnitId = urlParam('ou');
	if (orgUnitId) {
		setHref('forgetPasswordQuestionAnswer', `reset-password-form?ou=${orgUnitId}`);
		setHref('signupLink', `signup?ou=${orgUnitId}`);
	}

	(async function () {
		const config = await request('GET', 'config.json');
		mxLogin.baseUrl = config.restHosts.default ?? location.origin;

		const wmConfig = await getWmConfig();

		if (config.mode === 'dev') {
			Object.assign(mxLogin.config, wmConfig, config);
		} else {
			Object.assign(mxLogin.config, config, wmConfig);
		}

		mxLogin.initialSettingsUrl = `${mxLogin.baseUrl}/api/login/params`;
		mxLogin.winAuthUrl = `${mxLogin.baseUrl}/api/login/win`;
		mxLogin.signOutUrl = 'https://accounts.matrix42.com/issue/oauth2/signout?redirect_uri=';

		telemetry = new Telemetry({
			token: mxLogin.config.telemetryToken,
			productVersion: mxLogin.config.productVersion,
			envId: mxLogin.config.envId,
			envType: mxLogin.config.envType,
			licenseId: mxLogin.config.licenseId,
			customerName: mxLogin.config.customerName,
			customerId: mxLogin.config.customerId,
			debugTelemetry: mxLogin.config.debugTelemetry,
			language: language,
		});

		const loginParamsPromise = request(
			'GET',
			`${mxLogin.initialSettingsUrl}?clientId=${mxLogin.config.authentication?.oauth2?.clientid}${orgUnitId ? `&ou=${orgUnitId}` : ''}`
		).catch(handleHttpError);

		const isAuthFlow = checkInitAuthFlow();
		if (isAuthFlow) {
			if (forceLoginPage) {
				redirectUrl += '&ForceLoginPage=true';
			}
			await init(loginParamsPromise);
		}
	})();

	function trackTelemetry(loginType) {
		telemetry.track({
			eventName: 'User Sign In button clicked',
			senderTitle: loginType,
		});
	}

	return {
		onBrowserCredentialsClick: onBrowserCredentialsClick,
		onSignInClick: onSignInClick,
		onSaml2SignInClick: onSaml2SignInClick,
		onOpenIdSignInClick: onOpenIdSignInClick,
		onM42SingleSignOnClick: onM42SingleSignOnClick,
		setLoading: setLoading,
		onAADSignOnClick: onAADSignOnClick,
		onEntraIdSignInClick: onEntraIdSignInClick,
	};

	async function getWmConfig() {
		try {
			return await request('GET', `${mxLogin.baseUrl}/api/wmConfig`);
		} catch (ex) {
			if (ex.status === 503) {
				// caused by Maintenance mode or no Licenses error - to be handled later
				return {};
			}
			const response = processResponse(ex.response);
			if (response.error) {
				setErrorMessage(response.error);
			}
		}
	}

	function handleHttpError(error) {
		let errorText;
		if (error && error.response && error.response.error_code === 'NO_VALID_CERTIFICATE') {
			errorText = isLangDE ? 'Es gibt keine gültigen Lizenz.' : 'No valid License found.';
		} else if (error && parseInt(error.status, 10) === 503) {
			errorText = isLangDE ? 'Wartungsmodus. Warten Sie bitte...' : 'Maintenance Mode. Please, wait...';
		} else {
			errorText = defaultErrorText;
		}
		setString('systemError', errorText);
		setVisibility('mxLoginLoading', false);
	}

	async function init(paramsPromise) {
		function storageListener(event) {
			if (event.key === tokenStorageItemKey && event.newValue) {
				// Token was added/updated in another tab
				const redirectUrl = sessionStorage.getItem(redirectUriStorageItemKey);

				if (redirectUrl) {
					sessionStorage.removeItem(redirectUriStorageItemKey);
					window.location.href = redirectUrl;
					return;
				}

				const authCfg = mxLogin.config.authentication;

				const homePageUrl = authCfg.oauth2.redirect_uri + (orgUnitId ? `?ou=${orgUnitId}` : '');
				window.location.href = homePageUrl;
			}
		}

		window.addEventListener('storage', storageListener);

		try {
			await applyCssVariables(mxLogin.config.restHosts.default);
			const loginParams = await paramsPromise;
			if (!loginParams) {
				return;
			}

			const m42Settings = {
				aadDirectories: loginParams.Directories,
				SsoEnabled: loginParams.SsoEnabled || false,
				StsUrl: loginParams.StsUrl || false,
				Saml2Enabled: loginParams.Saml2Enabled || false,
				SamlButtonTitle: loginParams.SamlButtonTitle || 'SAML2',
				Saml2SsoEnabled: (loginParams.SsoState ?? 0) === 1,
				OpenIdEnabled: loginParams.OpenIdEnabled || false,
				OpenIdButtonTitle: loginParams.OpenIdButtonTitle || 'OpenID',
				OpenIdSsoEnabled: (loginParams.SsoState ?? 0) === 2,
				NtlmEnabled: loginParams.NtlmEnabled || false,
				allowUserRegistration: loginParams.AllowUserRegistration,
				captchaEnabled: loginParams.CaptchaEnabled,
				LoginUiConfig: loginParams.LoginUiConfig || {},
				EntraIdTenantsOneButtonTitle: loginParams.EntraIdTenantsOneButtonTitle,
				EntraIdTenantsOneButton: loginParams.EntraIdTenantsOneButton,
				ShowOnlyEntraIdLoginButton: loginParams.ShowOnlyEntraIdLoginButton,
			};

			mxLogin.form = document.getElementById('login-form');
			mxLogin.form.action = `${mxLogin.baseUrl + m42Settings.StsUrl}?ReturnUrl=${encodeURIComponent(redirectUrl)}&ou=${orgUnitId}`;

			mxLogin.m42Settings = m42Settings;

			const authParams = (mxLogin.config.authentication || {}).params || {};
			mxLogin.m42Settings.AllowWindowsLogin = (isWindows || Boolean(authParams.allowNonWindowsAutoLogin)) && m42Settings.NtlmEnabled;
			mxLogin.m42Settings.AutoLogin = Boolean(authParams.autoLogin);

			mxLogin.localStrings = (loginParams.Localization && loginParams.Localization.login) || {};
			mxLogin.localStrings.signup = (loginParams.Localization && loginParams.Localization.signup) || {};
			mxLogin.localStrings.loginUiConfig = (loginParams.Localization && loginParams.Localization.loginUiConfig) || {};
			mxLogin.localStrings.basePageTitle = loginParams.PageTitle;

			if (m42Settings.NtlmEnabled) {
				mxLogin.winAuthUrl = mxLogin.baseUrl + loginParams.WinStsUrl + '?ReturnUrl=' + encodeURIComponent(redirectUrl);
				mxLogin.winAuthRerun = false;
			}

			if (mxLogin.localStrings.otherError) {
				window.localStorage.setItem('mx.login.defaultErrorText', mxLogin.localStrings.otherError);
			} else {
				window.localStorage.removeItem('mx.login.defaultErrorText');
			}

			if (isSignOut) {
				signOut();
			}

			if (!errorFromParams && m42Settings.Saml2Enabled && m42Settings.Saml2SsoEnabled && !forceLoginPage) {
				return onSaml2SignInClick();
			}

			if (!errorFromParams && m42Settings.OpenIdEnabled && m42Settings.OpenIdSsoEnabled && !forceLoginPage) {
				return onOpenIdSignInClick('GCSettings');
			}

			mxLogin.showOnlyEntraIdLoginButton = !forceLoginPage && m42Settings.ShowOnlyEntraIdLoginButton;

			if (!mxLogin.showOnlyEntraIdLoginButton && m42Settings.allowUserRegistration) {
				const signupPanel = document.getElementById('signupPanel');
				const resetPasswordPanel = document.getElementById('resetPasswordPanel');

				setString('signupQuestion', mxLogin.localStrings.signUpQuestion);
				setString('signupLink', mxLogin.localStrings.signUpLink);
				setString('forgetPasswordQuestion', mxLogin.localStrings.forgetPasswordQuestion);
				setString('forgetPasswordQuestionAnswer', mxLogin.localStrings.forgetPasswordQuestionAnswer);

				signupPanel.style.display = 'block';
				resetPasswordPanel.style.display = 'block';
			}

			if (m42Settings.captchaEnabled) {
				m42Settings.captchaActivated = isCaptchaActivated();
			}

			initUI();

			if (mxLogin.m42Settings.AutoLogin && mxLogin.m42Settings.AllowWindowsLogin && !errorFromParams && !isSignOut) {
				await winAuth();
			}
		} catch (ex) {
			handleHttpError(ex);
		}
		setVisibility('mxLoginLoading', false);
		$('#mx-aria-initial-loading-msg').remove();
	}

	function checkInitAuthFlow() {
		if (isSignOut) {
			signOut();
		}
		const authCfg = mxLogin.config.authentication;

		if (!authCfg) {
			return false;
		}

		const clientId = urlParam('client_id');
		if (!clientId) {
			if (isSignOut) {
				window.localStorage.setItem(localStorageSignoutKey, true);
			}
			window.location.href = authCfg.oauth2.redirect_uri + (orgUnitId ? `?ou=${orgUnitId}` : '');
			return false;
		}

		return checkObsoleteAuthFlow(clientId);
	}

	function checkObsoleteAuthFlow(clientId) {
		const authCfg = mxLogin.config.authentication;

		if (!redirectUrl || authorizePathIncluded(redirectUrl)) {
			const scope = urlParam('scope') || authCfg.oauth2.scope;
			const authLocation = authCfg.oauth2.endpoints.authorize + '?client_id=' + clientId + '&scope=' + scope + '&response_type=token';

			const initLoginParams = [];
			if (isSignOut) {
				initLoginParams.push('redirect_to_signout=true');
			}
			if (forceLoginPage) {
				initLoginParams.push('ForceLoginPage=true');
			}
			if (errorFromParams) {
				initLoginParams.push('error=' + encodeURIComponent(errorFromParams));
			}
			if (orgUnitId) {
				initLoginParams.push(`ou=${orgUnitId}`);
			}

			window.location.href = authLocation + (initLoginParams.length > 0 ? '&' + initLoginParams.join('&') : '');
			return false;
		}

		return true;
	}

	function authorizePathIncluded(url) {
		let authorizePath = new URL(mxLogin.config.authentication.oauth2.endpoints.authorize).pathname;
		if (authorizePath.indexOf('/') !== 0) {
			authorizePath = '/' + authorizePath;
		}

		return url.indexOf(authorizePath) !== 0;
	}

	function signIn(trackedLoginType = 'LDAP') {
		let validated = true;
		if (mxLogin.m42Settings.captchaActivated) {
			validated = validateCaptcha(mxLogin.captchaCode);
		}
		if (validated) {
			trackTelemetry(trackedLoginType);
			mxLogin.form.submit();
		} else {
			setTimeout(() => {
				setLoading(false);
				setErrorMessage('wrong_captcha');
				get('password').value = '';
				get('captcha-code').value = '';
				mxLogin.captchaCode = renderCaptcha();
			}, 1000);
		}
	}

	function onSignInClick() {
		mxLogin.isMainForm ? onSignInWithSTS('Force', 'LDAP') : onEntraIdSignInClick();
	}

	function onSaml2SignInClick() {
		onSignInWithSTS('SAML2', 'SAML2');
	}

	function onOpenIdSignInClick(oAuth2ConfId) {
		addHiddenAttribute('OAuth2ConfId', oAuth2ConfId);
		onSignInWithSTS('OAUTH2ACCOUNT', 'OpenId Connect');
	}

	function onBrowserCredentialsClick() {
		trackTelemetry('BrowserCredentials');
		return winAuth();
	}

	function winAuth() {
		setLoading(true);
		return request('GET', mxLogin.winAuthUrl)
			.then(function (response) {
				mxLogin.winAuthRerun = false;
				let authToken = response && response.access_token;
				authToken = authToken ? '#access_token=' + authToken : '';

				if (response.RedirectUri) {
					//for STS
					window.location.href = response.RedirectUri + authToken;
				} else if (redirectUrl) {
					window.location.href = redirectUrl + authToken;
				} else {
					window.location.href = location.origin + authToken;
				}
			})
			.catch(function (err) {
				// Solution for PRB35919: UUX - SSO is not correct working under certain circumstances with chrome based browsers
				// Corrected the situation when auto logon for win authentication does not work sometimes on first request after clear browser cache.
				if (!mxLogin.winAuthRerun && err.target instanceof XMLHttpRequest && parseInt(err.target.status, 10) === 0 && err.target.response === null) {
					mxLogin.winAuthRerun = true;
					winAuth();
					return;
				}

				setLoading(false);
				if (!(err instanceof XMLHttpRequest)) {
					return;
				}
				if (parseInt(err.status, 10) === 401) {
					return;
				}

				const response = processResponse(err.response);
				if (typeof response === 'object' && response.error) {
					setErrorMessage(response.error);
				}
			});
	}

	function setLoading(enabled) {
		setClass('loginFormDisabled', 'show', enabled);
		setClass('loginForm', 'disabled', enabled);

		const buttons = Array.prototype.slice.call(document.querySelectorAll('.login-button'));
		buttons.forEach(function (button) {
			setAttribute(button, 'disabled', enabled ? '' : null);
			if (!enabled) {
				setClass(button, 'mx-loading-bg', enabled);
				setString('signIn', mxLogin.localStrings.signIn);
				setString('useMws', mxLogin.localStrings.useMWS);
				setString('saml2SignIn', mxLogin.m42Settings.SamlButtonTitle);
				setString('openIdSignIn', mxLogin.m42Settings.OpenIdButtonTitle);
				setString('useBrowserCredentials', mxLogin.localStrings.useBrowserCredentials);
			}
		});
	}

	function onM42SingleSignOnClick() {
		onSignInWithSTS('OAUTH2ACCOUNT', 'M42');
	}

	function onSignInWithSTS(logonType, trackedLoginType) {
		addHiddenAttribute('logonType', logonType);
		signIn(trackedLoginType);
	}

	function signOut() {
		window.localStorage.removeItem('mx.auth.session.token');

		if (mxLogin.m42Settings.SsoEnabled) {
			const redirectedFromAccounts = cookieValue(redirectedFromAccountsCookieName) === 'true';
			if (redirectedFromAccounts) {
				let url = window.location.href;
				const urlParts = url.split('?');
				const domainPart = urlParts.shift();
				let qPart = urlParts.join('?');
				qPart = removeUrlParam('redirect_to_signout', qPart);

				url = domainPart + '?' + qPart;

				window.location.href = mxLogin.signOutUrl + encodeURIComponent(url);
			}
		}
	}

	function setButtonHandlers() {
		get('loginForm').addEventListener('click', function (event) {
			const buttonElement = event.target.closest('.login-button');
			if (buttonElement) {
				setClass(buttonElement, 'mx-loading-bg', true);
				setString(buttonElement, '');
				setAttribute(buttonElement, 'disabled', '');
				setLoading(true);
			}
		});
	}

	function onEnterEvents() {
		get('username').addEventListener('keydown', function (event) {
			if (event.keyCode === 13) {
				if (!event.target.value) {
					return;
				}

				if (!mxLogin.isMainForm) {
					get('entraIdSignInBtn').click();
				} else if (get('password').value) {
					get('signIn').click();
				} else {
					get('password').focus();
				}
			}
		});
		get('password').addEventListener('keydown', function (event) {
			if (event.keyCode === 13) {
				if (!event.target.value) {
					return;
				}

				if (get('username').value) {
					get('signIn').click();
				} else {
					get('username').focus();
				}
			}
		});
	}

	function fixBugWithMaterialPwdInput() {
		setTimeout(function () {
			// About the issue: https://github.com/google/material-design-lite/issues/1253
			function checkWebkitAutofill() {
				document.querySelectorAll('.mdl-textfield__input:-webkit-autofill').forEach(function (el) {
					el.parentElement.classList.add('is-dirty');
				});
			}

			for (let i = 0; i < 5; i++) {
				// several calls just for more responsive UI behaviour
				setTimeout(checkWebkitAutofill, 10 + i * 350);
			}

			if (get('username').value) {
				get('password').focus();
			} else {
				get('username').focus();
			}
		}, 1);
	}

	function initUI() {
		window.document.title = mxLogin.localStrings.basePageTitle + '  ' + mxLogin.localStrings.pageTitle;

		const uiCfg = mxLogin.m42Settings.LoginUiConfig;

		setAttribute('Logo', 'src', uiCfg.Logo);

		if (mxLogin.localStrings.loginUiConfig.WelcomeTitle || mxLogin.localStrings.loginUiConfig.WelcomeText) {
			setVisibility('WelcomeBlock', true);
			if (mxLogin.localStrings.loginUiConfig.WelcomeTitle) {
				setVisibility('WelcomeTitle', true);
				get('WelcomeTitle').textContent = mxLogin.localStrings.loginUiConfig.WelcomeTitle;
				setStyle('WelcomeTitle', 'color', uiCfg.WelcomeTitleColor);
			}
			if (mxLogin.localStrings.loginUiConfig.WelcomeText) {
				setVisibility('WelcomeDescr', true);
				get('WelcomeDescr').textContent = mxLogin.localStrings.loginUiConfig.WelcomeText;
			}
		}

		if (uiCfg.BlurBackgroundImage) {
			setStyle('pageBgOverlay', 'backdropFilter', 'blur(10px)');
		}

		if (uiCfg.ShowRightPanel === true) {
			setVisibility('promoBlock', true);
			setStyle('promoBlock', 'backgroundColor', uiCfg.RightForegroundColor);
			setStyle('promoBlock', 'backgroundImage', 'url(' + uiCfg.RightForegroundImage + ')');

			// 0/notSet - stretch/cover (default); 1 - repeat; 2 - original size)
			switch (uiCfg.RightForegroundImageBehaviour) {
				case 1:
					setClass('promoBlock', 'bg-repeat', true);
					break;
				case 2:
					setClass('promoBlock', 'bg-origin-size', true);
					break;
			}
		} else {
			setStyle('WelcomeTitle', 'textAlign', 'center');
			setStyle('WelcomeDescr', 'textAlign', 'center');
		}

		setStyle('pageBg', 'backgroundColor', uiCfg.BackgroundColor);
		setStyle('loginForm', 'backgroundColor', uiCfg.BackgroundColor);
		setStyle('pageBg', 'backgroundImage', 'url(' + uiCfg.BackgroundImage + ')');
		setStyle('pageBgOverlay', 'backgroundColor', '#00000080');

		setStyle('login-form', 'backgroundColor', uiCfg.LeftForegroundColor);
		setStyle('login-form', 'color', uiCfg.TextColor);
		setStyle('login-form', 'backgroundImage', 'url(' + uiCfg.LeftForegroundImage + ')');

		setLoginThemeStyles(uiCfg);

		setAttribute('loginForm', 'aria-label', mxLogin.localStrings.ariaPageDescription);
		setString('userNameLabel', mxLogin.localStrings.userName);
		setString('passwordLabel', mxLogin.localStrings.password);
		setString('useBrowserCredentials', mxLogin.localStrings.useBrowserCredentials);
		setString('winAuthPanel_or', mxLogin.localStrings.or);
		setString('saml2SignInPanel_or', mxLogin.localStrings.or);
		setString('openIdSignInPanel_or', mxLogin.localStrings.or);
		setString('m42SsoPanel_or', mxLogin.localStrings.or);
		setString('entraIdSignInPanel_or', mxLogin.localStrings.or);
		setString('signIn', mxLogin.localStrings.signIn);
		setString('useMws', mxLogin.localStrings.useMWS);

		if (!mxLogin.showOnlyEntraIdLoginButton) {
			if (!mxLogin.m42Settings.EntraIdTenantsOneButton || mxLogin.m42Settings.aadDirectories.length === 1) {
				aadPanel = document.getElementById('aadPanel');
				aadPanel.style.display = 'block';
				mxLogin.m42Settings.aadDirectories.forEach(addAADLoginButton);
			}

			if (mxLogin.m42Settings.EntraIdTenantsOneButton && mxLogin.m42Settings.aadDirectories.length > 1) {
				setVisibility('entraIdSignInPanel', true);
				setString('entraIdSignInBtn', mxLogin.m42Settings.EntraIdTenantsOneButtonTitle);
			}

			if (mxLogin.m42Settings.Saml2Enabled && !forceLoginPage) {
				setVisibility('saml2SignInPanel', true);
				setString('saml2SignIn', mxLogin.m42Settings.SamlButtonTitle);
			}

			if (mxLogin.m42Settings.OpenIdEnabled && !forceLoginPage) {
				setVisibility('openIdSignInPanel', true);
				setString('openIdSignIn', mxLogin.m42Settings.OpenIdButtonTitle);
			}

			setVisibility('winAuthPanel', mxLogin.m42Settings.AllowWindowsLogin);
			setVisibility('m42SsoPanel', mxLogin.m42Settings.SsoEnabled);
		} else {
			setVisibility('entraIdSignInPanel', true);
			setString('entraIdSignInBtn', mxLogin.m42Settings.EntraIdTenantsOneButtonTitle);
			switchOnlyEntraIdForm();
		}

		setLoading(false);
		setVisibility('loginForm', true);

		setButtonHandlers();
		onEnterEvents();
		fixBugWithMaterialPwdInput();

		if (errorFromParams) {
			setErrorMessage(errorFromParams);
		}

		setVisibility('captcha', mxLogin.m42Settings.captchaActivated);
		if (mxLogin.m42Settings.captchaActivated) {
			setString('captchaLabel', mxLogin.localStrings.captchaLabel);
			mxLogin.captchaCode = renderCaptcha();
		}

		handleEntraIdSignFormEvents();
	}

	function setErrorMessage(err) {
		let errMsg;

		switch (err) {
			case 'invalid_grant':
				errMsg = mxLogin.localStrings.invalidLoginOrPassword;
				break;
			case 'account_locked':
				errMsg = mxLogin.localStrings.accountLocked;
				break;
			case 'invalidRedirectUrl':
				errMsg = mxLogin.localStrings.invalidRedirectUrl;
				break;

			case 'wrong_captcha':
				errMsg = mxLogin.localStrings.wrongCaptcha;
				break;

			case 'signup.userExists':
			case 'signup.userNotExists':
			case 'signup.tokenInvalid':
			case 'signup.emailIncorrect':
			case 'signup.registrationFailed':
			case 'signup.resetPasswordFailed':
			case 'signup.resetPasswordNotSupport':
			case 'signup.accountNotFound':
			case 'signup.accountNotActive': {
				const signupErrCode = err.split('.')[1];
				errMsg = mxLogin.localStrings.signup[signupErrCode] || mxLogin.localStrings.otherError || defaultErrorText;
				break;
			}
			default:
				errMsg = mxLogin.localStrings.otherError || defaultErrorText;
				break;
		}

		setVisibility('mxLoginLoading', false);
		setString(!isHidden('login-form') ? 'resultLabel' : 'systemError', errMsg);
		console.error(err);
	}

	function setString(idOrEl, string) {
		const element = typeof idOrEl === 'string' ? get(idOrEl) : idOrEl;
		if (!element) {
			return;
		}
		if (element.tagName === 'INPUT') {
			element.value = string;
		} else if (element.tagName === 'BUTTON') {
			element.title = string;
			const textNode = element.childNodes[0];
			if (textNode && textNode.nodeType === 3 /* is text node */) {
				textNode.nodeValue = string;
			} else {
				element.innerHTML = string;
			}
		} else if ('innerHTML' in element) {
			element.innerHTML = string;
		}
	}

	function setHref(id, href) {
		const element = get(id);
		if (!element || element.tagName !== 'A') {
			return;
		}
		element.href = href;
	}

	function setVisibility(id, visible) {
		const element = get(id);
		if (element) {
			element.style.display = visible ? 'block' : 'none';
		}
	}

	function setClass(id, className, enabled) {
		const element = id instanceof HTMLElement ? id : get(id);
		if (!element) {
			return false;
		}
		const hasClass = element.classList.contains(className);
		if (enabled && !hasClass) {
			element.classList.add(className);
		} else if (hasClass) {
			element.classList.remove(className);
		}
		return true;
	}

	// if value is null or undefined then the attribute will be removed
	function setAttribute(id, attributeName, value) {
		const element = id instanceof HTMLElement ? id : get(id);
		if (!element) {
			return false;
		}
		if (typeof value !== 'undefined' && value !== null) {
			element.setAttribute(attributeName, value);
		} else {
			element.removeAttribute(attributeName);
		}
		return true;
	}

	function setStyle(id, property, value) {
		if (typeof value !== 'undefined') {
			const element = get(id);
			if (element) {
				element.style[property] = value;
			}
		}
	}

	function isHidden(id) {
		return !document.getElementById(id).offsetParent;
	}

	function getContrastColor(bgColorArg, lightColor, middleColor, darkColor) {
		const bgColor = bgColorArg.slice(1);
		const r = parseInt(bgColor.substr(0, 2), 16);
		const g = parseInt(bgColor.substr(2, 2), 16);
		const b = parseInt(bgColor.substr(4, 2), 16);

		const lightness = (r * 299 + g * 587 + b * 114) / 1000;
		return lightness < 35 || lightness > 200 ? middleColor : lightness < 140 ? lightColor : lightness >= 140 ? darkColor : '';
	}

	function request(method, url, params, config) {
		return new Promise(function (resolve, reject) {
			const xhr = new XMLHttpRequest();
			xhr.open(method, url, true);
			xhr.responseType = 'json';
			xhr.setRequestHeader('Accept', 'application/json');
			xhr.setRequestHeader('Explicit-Language', language);
			if (config) {
				config(xhr);
			}

			xhr.onload = function () {
				const status = parseInt(xhr.status, 10);
				if (status === 200) {
					setString('resultLabel', '');
					resolve(processResponse(xhr.response));
				}
				reject(xhr); // ???
			};

			xhr.ontimeout = function (err) {
				reject(err);
			};
			xhr.onerror = function (err) {
				reject(err);
			};

			xhr.send(params || null);
		});
	}

	function processResponse(response) {
		try {
			return typeof response === 'string' ? JSON.parse(response) : response;
		} catch (e) {
			return response;
		}
	}

	function removeUrlParam(name, url) {
		name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
		const regex = new RegExp('[\\?&]?' + name + '=([^&#]*)[&]?');
		return url.replace(regex, '');
	}

	function cookieValue(name) {
		const value = '; ' + window.document.cookie;
		const parts = value.split('; ' + name + '=');
		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		}
	}

	function addHiddenAttribute(name, value) {
		const i = document.createElement('input');
		i.setAttribute('type', 'hidden');
		i.setAttribute('name', name);
		i.setAttribute('value', value);
		mxLogin.form.appendChild(i);
	}

	function onAADSignOnClick(tenantId) {
		addHiddenAttribute('DirectoryId', tenantId);
		onSignInWithSTS('SAML2', 'AAD');
	}

	function switchEntraIdForm(isMainForm) {
		mxLogin.isMainForm = isMainForm;
		if (isMainForm) {
			get('mxLoginFormContent').classList.remove('mx-login-mode--EntraIdForm');
			get('userNameLabelRequiredMark').classList.remove('asterisk-visible');
			get('entraIdSignInBtn').classList.remove('mdl-button--raised');
		} else {
			get('username').focus();
			get('mxLoginFormContent').classList.add('mx-login-mode--EntraIdForm');
			get('userNameLabelRequiredMark').classList.remove('asterisk-visible');
			setTimeout(() => {
				get('userNameLabelRequiredMark').classList.add('asterisk-visible');
			});
			get('entraIdSignInBtn').classList.add('mdl-button--raised');
		}
	}

	function switchOnlyEntraIdForm() {
		setVisibility('signIn', false);
		setVisibility('entraIdSignInPanel_or', false);
		setVisibility('passwordPanel', false);
		get('entraIdSignInBtn').classList.add('mdl-button--raised');
		get('username').focus();
		get('username').value = '';
	}

	function onEntraIdSignInClick() {
		const username = get('username').value;
		if (username) {
			get('mxLoginFormContent').classList.add('mx-login-mode--EntraIdForm');
			setClass('entraIdSignInBtn', 'mx-loading-bg', true);
			setString('entraIdSignInBtn', '');
			setAttribute('entraIdSignInBtn', 'disabled', true);

			addHiddenAttribute('EntraIdUpn', username);
			onSignInWithSTS('SAML2', 'SAML2');
		} else if (!mxLogin.showOnlyEntraIdLoginButton) {
			switchEntraIdForm(false);
		}
	}

	function handleEntraIdSignFormEvents() {
		get('username').addEventListener('blur', () => {
			if (!mxLogin.isMainForm) {
				setTimeout(() => {
					if (document.activeElement.id !== 'entraIdSignInBtn') {
						switchEntraIdForm(true);
					}
				});
			}
		});

		document.addEventListener('keydown', (event) => {
			if (!mxLogin.isMainForm && event.key === 'Escape') {
				switchEntraIdForm(true);
			}
		});
	}

	function addAADLoginButton(directory) {
		const template =
			'<div class="divider" aria-hidden="true">' +
			mxLogin.localStrings.or +
			'</div>' +
			'<div class="windowsBtn">' +
			'<button ' +
			' onclick="return mxLogin.onAADSignOnClick(\'' +
			directory.DirectoryId +
			'\')"' +
			' class="mdl-button mdl-js-button  mdl-js-ripple-effect login-button"' +
			' type="button"' +
			' id="aad-' +
			directory.DirectoryId +
			'"' +
			' title="' +
			directory.LoginButtonTitle +
			'"' +
			'>' +
			directory.LoginButtonTitle +
			'</button>' +
			'</div>';

		aadPanel.insertAdjacentHTML('beforeend', template);
	}

	function setLoginThemeStyles(uiCfg) {
		let styles =
			'.mx-unauth-page {' +
			'--mx-content-bg-color: ' +
			uiCfg.LeftForegroundColor +
			';' +
			'--mx-content-text-color: ' +
			uiCfg.TextColor +
			';' +
			'--mx-faded-color: color-mix(in srgb, var(--mx-content-text-color) 67%, transparent);' +
			'--mx-active-color:' +
			(uiCfg.AccentuationColor || getContrastColor(uiCfg.LeftForegroundColor, '#ffb66e', '#CA5000', '#672100')) +
			';' +
			'--mx-error-text-color: ' +
			getContrastColor(uiCfg.LeftForegroundColor, '#FFBBBB', '#EE0000', '#550000') +
			';' +
			'}';

		styles += '.mdl-button--raised { background-color:' + uiCfg.ButtonColor + '; color: ' + uiCfg.ButtonTextColor + ';}';
		styles +=
			'.mdl-button--raised:hover, .mdl-button--raised:focus:not(:active), .mdl-button--raised:active {' +
			'background-color: color-mix(in srgb, ' +
			uiCfg.ButtonColor +
			' 88%, black);}';

		setString('loginThemeStyles', styles);
	}
}
